import { useCallback } from 'react';

import { useTheme } from '@mui/material';
import { XAxisOptions } from 'highcharts';

import useRegularOptions from '@/components/Chart/hooks/options/useRegularOptions';
import getRegularTooltip from '@/components/Chart/hooks/tooltip/getRegularTooltip';
import useForwardCurveTooltipFormatter from '@/components/Chart/hooks/tooltip/useForwardCurveTooltipFormatter';
import { ExtendedOptions, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { regularChartPalette } from '@/constants/constants';

const useForwardCurveOptions = (height?: number) => {
  const { createOptions: createRegularOptions } = useRegularOptions(height);
  const { createTooltipFormatter } = useForwardCurveTooltipFormatter();
  const theme = useTheme();
  const { tooltip } = getRegularTooltip(createTooltipFormatter, theme);
  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[]): ExtendedOptions => {
      chartSeries = chartSeries.map(series => {
        const { combinedIndex } = series.custom;
        series.color ??= regularChartPalette[combinedIndex % regularChartPalette.length];
        return series;
      });

      let options = {
        ...createRegularOptions(chartSeries),
        navigator: { enabled: false },
        rangeSelector: { enabled: false }
      };

      const xAxisOptions = options.xAxis as XAxisOptions | undefined;
      if (xAxisOptions) {
        xAxisOptions.type = 'linear';
        xAxisOptions.tickInterval = 1;
        xAxisOptions.crosshair = true;
        if (xAxisOptions.labels?.formatter) xAxisOptions.labels.formatter = undefined;
      }
      options = { ...options, xAxis: xAxisOptions };
      options = { ...options, tooltip };
      options = {
        ...options,
        chart: { ...options.chart, marginTop: 40, zooming: { type: undefined } }
      };
      options = {
        ...options,
        plotOptions: {
          ...options.plotOptions,
          series: {
            ...options.plotOptions?.series,
            dataLabels: {
              enabled: false
            }
          }
        }
      };
      return options;
    },
    [createRegularOptions, tooltip]
  );

  return { createOptions };
};

export default useForwardCurveOptions;
